import { useContext, useEffect, useLayoutEffect } from "react"
import { ThemingInjectorScenario } from "../../utility/theming"
import ConfigContext from "../ConfigContext/ConfigContext"
import {
    selectCurrentProjectState,
    selectDefaultProjectSettingsState,
    selectProjectSettings,
    selectThemeInitializingStatus
} from "../../store/projects/selectors"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { getDefaultProjectSettings } from "../../store/projects/thunks"
import { actions } from "../../store/projects/slice"
import { DEFAULT_SYSTEM_PROJECT_SETTINGS } from "../ProjectSettings/System/defaultSettings"
import useOmniLoader from "../../hooks/useOmniLoader"

interface Props {
    isAuthorized: boolean
    isInject?: boolean
}

export const ThemingInjector: React.FC<Props> = ({ isAuthorized, isInject = true }) => {
    const dispatch = useAppDispatch()
    const { WebConfig } = useContext(ConfigContext)
    const isRootProject = WebConfig.isDefault
    const themeInitStatus = useAppSelector(selectThemeInitializingStatus)
    const projectState = useAppSelector(selectCurrentProjectState)
    const settings = useAppSelector(selectProjectSettings)
    const rootProjectSettings = useAppSelector(selectDefaultProjectSettingsState)
    const rootSysSettings = rootProjectSettings.data?.Settings.System

    const isAllowedInNonRoot =
        settings?.System?.ColorSchema?.ColorSchemaUsing && rootSysSettings?.ColorSchemaChangeAllow

    const colorSchema =
        (isAuthorized
            ? isRootProject || isAllowedInNonRoot
                ? settings?.System?.ColorSchema
                : rootSysSettings?.ColorSchema
            : WebConfig.themeSettings?.fallbackColorSchema) ?? DEFAULT_SYSTEM_PROJECT_SETTINGS.System.ColorSchema

    useEffect(() => {
        if (!isRootProject && isAuthorized && !rootProjectSettings.data) {
            dispatch(getDefaultProjectSettings())
        }
    }, [dispatch, isAuthorized, isRootProject, rootProjectSettings.data])

    useEffect(() => {
        // condition to avoid unexpected rerender
        if (!themeInitStatus.data) dispatch(actions.getThemeInitializingStatusProcess())
    }, [dispatch, themeInitStatus.data])

    useLayoutEffect(() => {
        if ((colorSchema && settings?.System) || !isAuthorized) {
            ThemingInjectorScenario.initializeColors(colorSchema)
            dispatch(actions.getThemeInitializingStatusSuccess(true))
        }

        if (!WebConfig.themeSettings) {
            return
        }

        ThemingInjectorScenario.run(WebConfig.themeSettings)
    }, [projectState.data, WebConfig.themeSettings, colorSchema, settings, isAuthorized, dispatch])

    useOmniLoader(themeInitStatus.inProcess, isAuthorized, isInject)

    return null
}
